import { createRouter, createWebHistory } from "vue-router";
import CryptoJS from "crypto-js";

// Components
import dashboardView from "@/views/dashboard_one/dashboardView.vue";
import landingPage from "@/views/landing_view/landing_page.vue";
import loginView from "@/components/Login/LoginView.vue";
import ProfileSection from "@/components/Profile/ProfileSection.vue";
import EditProfile from "@/components/EditProfile/EditProfile.vue";
import ResetPassword from "@/components/ResetPassword/ResetPassword.vue";
import PasswordResetConfirm from "@/components/PasswordResetConfirm/PasswordResetConfirm.vue";
import PasswordResetComplete from "@/components/PasswordResetComplete/PasswordResetComplete.vue";
import OneTimePin from "@/components/OneTimePin/OneTimePin.vue";
import UserManagementSection from "@/components/UserManagement/UserManagementSection.vue";

const routes = [
  {
    path: "/home",
    name: "dashboard_view",
    component: dashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: "/",
    redirect: "/login",
  },
  {
    path: "/landing",
    name: "landing_page",
    component: landingPage,
  },
  {
    path: "/login",
    name: "login",
    component: loginView,
  },
  {
    path: "/one_time_pin",
    name: "one_time_pin",
    component: OneTimePin,
  },
  {
    path: "/reset_password",
    name: "reset_password",
    component: ResetPassword,
  },
  {
    path: "/reset-password",
    alias: "/password_reset_confirm",
    name: "password_reset_confirm",
    component: PasswordResetConfirm,
  },
  {
    path: "/password_reset_complete",
    name: "password_reset_complete",
    component: PasswordResetComplete,
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileSection,
    meta: { requiresAuth: true }
  },
  {
    path: "/edit_profile",
    name: "edit_profile",
    component: EditProfile,
    meta: { requiresAuth: true }
  },
  {
    path: "/user_management",
    name: "user_management",
    component: UserManagementSection,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
];

// Get encryption key from environment or use default fallback
const encryption_key = process.env.VUE_APP_ENCRYPTION_KEY || "default-secret-key-change-in-production";
const get_profile_url = process.env.VUE_APP_VIEW_USER_URL;
const EXTERNAL_AUTH_QUERY_KEYS = [
  "token",
  "auth_token",
  "django_auth_token",
  "user",
  "user_data",
  "payload",
  "response",
  "profile",
  "profile_data",
  "role",
  "user_id",
  "username",
  "email",
  "first_name",
  "last_name",
  "organization",
  "phone_number"
];

export function set_token(token) {
    if (!token) {
      throw new Error('Token is required');
    }
    var encrypted_token = CryptoJS.AES.encrypt(token, encryption_key).toString();
    sessionStorage.setItem("encrypted_token", encrypted_token);
}

export function get_token() {
  try {
    var encrypted_token = sessionStorage.getItem("encrypted_token");

    if (!encrypted_token) {
      return null;
    }

    var decrypted_token = CryptoJS.AES.decrypt(
      encrypted_token,
      encryption_key
    );

    var token = decrypted_token.toString(CryptoJS.enc.Utf8);
    
    if (!token) {
      throw new Error('Failed to decrypt token - possibly wrong encryption key');
    }

    return token;
  } catch (error) {
    return null;
  }
}

export function set_user_details(user_details) {
  try {
    const role = (
      user_details.role_type ||
      user_details.role ||
      user_details.user_role?.role?.name ||
      ""
    ).toString();

    const normalized_role = role.toLowerCase().replace(/_/g, " ").trim();

    const is_admin_user = Boolean(
      user_details.is_superuser === true ||
      user_details.is_staff === true ||
      normalized_role === "admin"
    );

    const user_id = user_details.user_id || user_details.id || "";
    const last_name = user_details.last_name || "";
    const first_name = user_details.first_name || "";

    sessionStorage.setItem("role", role);
    sessionStorage.setItem("user_id", user_id);
    sessionStorage.setItem("last_name", last_name);
    sessionStorage.setItem("first_name", first_name);
    sessionStorage.setItem("is_admin_user", String(is_admin_user));
  } catch (error) {
    console.error("Error setting user details:", error);
  }
}

function read_query_value(query, keys = []) {
  for (const key of keys) {
    const value = query[key];
    if (Array.isArray(value) && value.length > 0 && value[0] !== undefined && value[0] !== null && String(value[0]).trim() !== "") {
      return String(value[0]).trim();
    }

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  return null;
}

function safe_parse_json_value(raw_value) {
  if (!raw_value || typeof raw_value !== "string") {
    return null;
  }

  const candidates = [raw_value];

  try {
    const decoded = decodeURIComponent(raw_value);
    if (decoded !== raw_value) {
      candidates.push(decoded);
    }
  } catch (error) {
    // Ignore decode errors and fall back to the raw value.
  }

  for (const candidate of candidates) {
    try {
      let parsed = JSON.parse(candidate);
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
      return parsed;
    } catch (error) {
      // Continue trying other candidates.
    }
  }

  return null;
}

function normalize_role(role_value) {
  return String(role_value || "").trim().toLowerCase().replace(/\s+/g, "_");
}

function normalize_user_profile_shape(user_profile = {}) {
  const profile_data = user_profile.profile || {};
  const normalized_phone_number =
    user_profile.phone_number ||
    user_profile.phoneNumber ||
    profile_data.phone_number ||
    profile_data.phoneNumber ||
    "";

  return {
    ...user_profile,
    phone_number: normalized_phone_number,
    profile: {
      ...profile_data,
      phone_number: normalized_phone_number
    }
  };
}

function build_external_user_profile(query = {}) {
  const response_data = safe_parse_json_value(read_query_value(query, ["response"])) || {};
  const response_user = response_data.user || {};
  const payload_data = safe_parse_json_value(read_query_value(query, ["payload", "user_data"])) || {};
  const direct_user_data = safe_parse_json_value(read_query_value(query, ["user"])) || {};
  const profile_data = safe_parse_json_value(read_query_value(query, ["profile", "profile_data"])) || {};

  const merged_profile_data = {
    ...(payload_data.profile_data || {}),
    ...(response_user.profile || {}),
    ...(direct_user_data.profile || {}),
    ...profile_data
  };

  const role_name =
    response_user.user_role?.role?.name ||
    direct_user_data.user_role?.role?.name ||
    payload_data.role ||
    direct_user_data.role ||
    read_query_value(query, ["role"]) ||
    "";

  const normalized_role = normalize_role(role_name);
  const user_id =
    response_user.id ||
    direct_user_data.id ||
    payload_data.id ||
    payload_data.user_id ||
    read_query_value(query, ["user_id"]);

  const first_name =
    response_user.first_name ||
    direct_user_data.first_name ||
    payload_data.first_name ||
    read_query_value(query, ["first_name"]) ||
    "";

  const last_name =
    response_user.last_name ||
    direct_user_data.last_name ||
    payload_data.last_name ||
    read_query_value(query, ["last_name"]) ||
    "";

  const email =
    response_user.email ||
    direct_user_data.email ||
    payload_data.email ||
    read_query_value(query, ["email"]) ||
    "";

  const username =
    response_user.username ||
    direct_user_data.username ||
    payload_data.username ||
    email ||
    read_query_value(query, ["username"]) ||
    "";

  return {
    ...(response_user || {}),
    ...(direct_user_data || {}),
    ...(payload_data || {}),
    id: user_id,
    username,
    email,
    first_name,
    last_name,
    role: role_name,
    role_key: normalized_role,
    phone_number: merged_profile_data.phone_number || read_query_value(query, ["phone_number"]) || "",
    profile: {
      ...(response_user.profile || {}),
      ...(direct_user_data.profile || {}),
      ...merged_profile_data,
      organization: merged_profile_data.organization || read_query_value(query, ["organization"]) || "",
      phone_number: merged_profile_data.phone_number || read_query_value(query, ["phone_number"]) || ""
    },
    user_role: response_user.user_role || direct_user_data.user_role || (
      role_name
        ? {
            role: {
              name: role_name
            }
          }
        : null
    )
  };
}

export function hydrate_session_from_external_auth(query = {}) {
  const token = read_query_value(query, ["token", "auth_token", "django_auth_token"]);

  if (!token) {
    return false;
  }

  const user_profile = build_external_user_profile(query);

  set_token(token);
  set_user_details({
    role_type: user_profile.user_role?.role?.name || user_profile.role || null,
    user_id: user_profile.id || null,
    first_name: user_profile.first_name || "",
    last_name: user_profile.last_name || ""
  });
  set_user_profile(user_profile);

  return true;
}

export function get_user_details() {
  var role = sessionStorage.getItem("role");
  var user_id = sessionStorage.getItem("user_id");
  var last_name = sessionStorage.getItem("last_name");
  var first_name = sessionStorage.getItem("first_name");

  var user_details = {
    role: role,
    user_id: user_id,
    last_name: last_name,
    first_name: first_name,
  };
  return user_details;
}

// NEW: Function to set complete user profile in sessionStorage
export function set_user_profile(user_profile) {
  try {
    const normalized_profile = normalize_user_profile_shape(user_profile);
    const encrypted_profile = CryptoJS.AES.encrypt(
      JSON.stringify(normalized_profile), 
      encryption_key
    ).toString();
    sessionStorage.setItem("encrypted_user_profile", encrypted_profile);
  } catch (error) {
    console.error('Error setting user profile:', error);
  }
}

// NEW: Function to get complete user profile from sessionStorage
export function get_user_profile() {
  try {
    const encrypted_profile = sessionStorage.getItem("encrypted_user_profile");
    
    if (!encrypted_profile) {
      return null;
    }

    const decrypted_profile = CryptoJS.AES.decrypt(
      encrypted_profile,
      encryption_key
    );

    const profile_string = decrypted_profile.toString(CryptoJS.enc.Utf8);
    
    if (!profile_string) {
      return null;
    }

    return normalize_user_profile_shape(JSON.parse(profile_string));
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

// NEW: Function to fetch and store user profile globally
export async function fetch_and_store_user_profile() {
  try {
    const user_details = get_user_details();
    const user_id = user_details.user_id;
    const auth_token = get_token();

    if (!user_id || !auth_token || !get_profile_url) {
      return null;
    }

    const base_profile_url = get_profile_url.endsWith("/")
      ? get_profile_url
      : `${get_profile_url}/`;
    const profile_url = `${base_profile_url}${user_id}/`;

    const response = await fetch(profile_url, {
      method: 'GET',
      headers: {
        'Authorization': `Token ${auth_token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const user_profile = safe_extract_user_data(data);
    
    // Store the complete profile in sessionStorage
    set_user_profile(user_profile);
    
    return user_profile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

// NEW: Safe extraction function for router
function safe_extract_user_data(apiResponse) {
  if (typeof apiResponse === 'string') {
    try {
      apiResponse = JSON.parse(apiResponse);
    } catch (e) {
      throw new Error('Invalid JSON response');
    }
  }
  
  let status;

  if (apiResponse.status !== undefined) {
    status = apiResponse.status;
  } else if (apiResponse['status'] !== undefined) {
    status = apiResponse['status'];
  }

  if (status && status !== 'success') {
    throw new Error(`API returned non-success status: ${status}`);
  }

  if (!status) {
    const direct_user = apiResponse.user || apiResponse.User || apiResponse;
    if (direct_user && (direct_user.id || direct_user.username || direct_user.email)) {
      return normalize_user_profile_shape({
        ...direct_user,
        phone_number:
          direct_user.phone_number ||
          direct_user.phoneNumber ||
          direct_user.profile?.phone_number ||
          direct_user.profile?.phoneNumber,
        is_first_login: direct_user.is_first_login !== undefined ? direct_user.is_first_login : direct_user.isFirstLogin,
        initial_complete_profile: direct_user.initial_complete_profile !== undefined ? direct_user.initial_complete_profile : direct_user.initialCompleteProfile
      });
    }
  }

  const data = apiResponse.data || apiResponse.Data || [];

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No user data found in response');
  }

  const first_item = data[0];
  const user_object = first_item.user || first_item.User || first_item;

  if (!user_object) {
    throw new Error('User data not found in response');
  }

  const user_profile = {
    ...user_object,
    phone_number:
      first_item.phone_number ||
      first_item.phoneNumber ||
      user_object.phone_number ||
      user_object.profile?.phone_number ||
      user_object.profile?.phoneNumber,
    is_first_login: first_item.is_first_login !== undefined ? first_item.is_first_login : first_item.isFirstLogin,
    initial_complete_profile: first_item.initial_complete_profile !== undefined ? first_item.initial_complete_profile : first_item.initialCompleteProfile
  };

  return normalize_user_profile_shape(user_profile);
}

export function is_authenticated() {
  const token = sessionStorage.getItem("encrypted_token");
  return token !== null && token !== undefined && token !== '';
}

export function clear_token() {
  sessionStorage.removeItem("encrypted_token");
  sessionStorage.removeItem("encrypted_user_profile");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("last_name");
  sessionStorage.removeItem("first_name");
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// NEW: Enhanced navigation guard to fetch profile on auth routes
router.beforeEach(async (to, from, next) => {
  const canAcceptExternalAuth = ["dashboard_view"].includes(to.name);
  const hasExternalAuthQuery = EXTERNAL_AUTH_QUERY_KEYS.some((key) => to.query[key] !== undefined);

  if (canAcceptExternalAuth && hasExternalAuthQuery) {
    const didHydrateExternalAuth = hydrate_session_from_external_auth(to.query);

    if (didHydrateExternalAuth) {
      next({
        path: "/home",
        query: {},
        replace: true
      });
      return;
    }
  }

  const isAuthenticated = is_authenticated();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

  if (requiresAuth && !isAuthenticated) {
    next("/login");
    return;
  }

  if (requiresAuth && isAuthenticated) {
    const existingProfile = get_user_profile();

    if (!existingProfile) {
      await fetch_and_store_user_profile();
    }
  }

  if (requiresAdmin && sessionStorage.getItem("is_admin_user") !== "true") {
    next("/home");
    return;
  }

  next();
});

export default router;
